import { buildCategoryTree, categoryMatches } from '../../lib/categoryTree.ts';
import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import { formatBuiltByShort } from '../../lib/format.ts';
import { UNCATEGORISED, ME, activeFilterCount, type DeckFilterState, type DeckSort } from './deckFilterState.ts';
import type { DeckSummary } from '../../../shared/deck.ts';
import type { CategoryEntry } from '../../../shared/api.ts';

// Re-exported so the route and the card keep one import for the sentinel; its
// home is deckFilterState because the URL codec has to know its wire form.
export { UNCATEGORISED };

type DeckFiltersInput = {
  decks: DeckSummary[];
  /** The category registry in display order. Entries are bare paths or
   *  `{ path, colour }`; the tree normalises both, so a rail row can carry its
   *  tint without this hook caring which form the file was written in. */
  categories: (string | CategoryEntry)[];
  /** The whole view, from useDeckFilterState. */
  filters: DeckFilterState;
  /** Slugs ticked in the grid. Survives filtering, so the counts here are the
   *  ones the bulk bar and its verbs must use. */
  selected: Set<string>;
  /** Injectable clock — the updated-window buckets are relative to it, and a
   *  test can't assert on "today" without pinning it. */
  now?: number;
  /** The signed-in person's id, for resolving the `me` owner sentinel. Null
   *  while /api/me is in flight, which makes "My decks" match nothing for that
   *  moment rather than matching everyone's. */
  meId?: string | null;
};

/** One entry in the Model filter's menu. `id` is what the filter state and the
 *  URL carry; `label` is what a person reads. */
export type DeckModelOption = {
  id: string;
  label: string;
  count: number;
};

export type DeckFilters = {
  visibleDecks: DeckSummary[];
  flatDecks: DeckSummary[];
  categoryTree: CategoryTreeNode[];
  allCategories: string[];
  uncategorisedCount: number;
  publishedCount: number;
  unpublishedCount: number;
  /** The design's word for the unpublished half of the same split. */
  draftCount: number;
  /** Every model stamped across the decks, commonest first. */
  models: DeckModelOption[];
  showTrash: boolean;
  filtering: boolean;
  activeFilters: number;
  selectedCount: number;
};

/**
 * The id a deck's build stamp filters under.
 *
 * The label alone won't do: `formatBuiltByShort` deliberately collapses model
 * ids to brands, so two genuinely different models can read the same, and it
 * contains a `·` that has no business in a URL. Engine plus raw model id is
 * both exact and stable.
 */
export function deckModelId(deck: DeckSummary): string | null {
  const b = deck.builtBy;
  if (!b || !b.engine || !b.model) return null;
  return `${String(b.engine).toLowerCase()}:${String(b.model).toLowerCase()}`;
}

/**
 * The earliest `lastEditedAt` that still counts as inside an updated window.
 *
 * "Today" is the calendar day, because that is what the word means to the
 * person reading it; the longer windows are rolling, because "edited in the
 * last month" is a span, not a calendar month.
 */
export function updatedWindowStart(window: DeckFilterState['updated'], now: number): number | null {
  const DAY = 24 * 60 * 60 * 1000;
  if (window === null) return null;
  if (window === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  if (window === 'week') return now - 7 * DAY;
  if (window === 'month') return now - 30 * DAY;
  return now - 90 * DAY; // quarter
}

/* A 14-day `openedWindowStart` lived here for the retired `opened` filter axis
 * (see deckFilterState.ts). `lastOpenedAt` itself is very much alive: it is
 * the `opened` SORT below and the Jump back in strip, neither of which needs a
 * window. */

/**
 * How each sort orders the grid.
 *
 * `edited` reproduces the server's own order (deck-scanner sorts newest edit
 * first), so it is the default and choosing it moves nothing. Array sort is
 * stable, so ties everywhere else keep that same server order underneath.
 */
export const DECK_SORT_COMPARATORS: Record<DeckSort, (a: DeckSummary, b: DeckSummary) => number> = {
  edited: (a, b) => b.lastEditedAt - a.lastEditedAt,
  // A deck nobody has opened since the stamp existed (`lastOpenedAt` in its
  // studio.json) has no answer to "when did you last open this", so it sorts
  // after every deck that does rather than pretending to be the oldest.
  opened: (a, b) => {
    if (a.lastOpenedAt === b.lastOpenedAt) return 0;
    if (a.lastOpenedAt === null) return 1;
    if (b.lastOpenedAt === null) return -1;
    return b.lastOpenedAt - a.lastOpenedAt;
  },
  name: (a, b) => (a.title || a.slug).localeCompare(b.title || b.slug, 'en-GB', { sensitivity: 'base' }),
  slides: (a, b) => b.slideCount - a.slideCount,
};

/**
 * Everything the dashboard derives from the loaded decks plus the current
 * filter state: the category tree, the counts, the model roster, and the decks
 * the grid actually shows, in the order it shows them. No state of its own —
 * pure derivation, recomputed each render.
 */
export function useDeckFilters({
  decks,
  categories,
  filters,
  selected,
  now,
  meId = null,
}: DeckFiltersInput): DeckFilters {
  const q = filters.query.trim().toLowerCase();
  // Bank decks (rkh-library etc., pipeline/banks.ts) are hidden here
  // entirely — they aren't presentations, and surfacing them as deck cards
  // confused everyone. Their home is the Library page (/library), linked
  // from the header.
  const visibleDecks = decks.filter((d) => d.kind !== 'bank');
  // ---- category tree ------------------------------------------------------
  // A deck carries ONE 1–2 segment path; a child deck also counts toward its
  // parent so a parent row shows itself + all its children.
  const uncategorisedCount = visibleDecks.filter((d) => !d.category).length;
  const { tree: categoryTree, allCategories } = buildCategoryTree(
    visibleDecks.map((d) => d.category),
    categories,
  );

  // Published = has a public share URL (matches the "published" card badge).
  // Publish counts are a stable split of ALL decks, not the current view, so the
  // chips read the same regardless of which category is selected.
  const isPublished = (d: DeckSummary) => !!d.publishedUrl;
  const publishedCount = visibleDecks.filter(isPublished).length;
  const unpublishedCount = visibleDecks.length - publishedCount;

  // ---- the model roster ---------------------------------------------------
  // Derived from what is actually on disk rather than from the engine list, so
  // the menu can only ever offer a filter that matches something. Decks with no
  // build stamp (imported, hand-made, pre-feature) contribute no option and are
  // excluded by any model filter.
  const modelCounts = new Map<string, DeckModelOption>();
  for (const d of visibleDecks) {
    const id = deckModelId(d);
    if (!id) continue;
    const found = modelCounts.get(id);
    if (found) found.count += 1;
    else modelCounts.set(id, { id, label: formatBuiltByShort(d.builtBy) || id, count: 1 });
  }
  const models = [...modelCounts.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const clock = now ?? Date.now();
  const updatedSince = updatedWindowStart(filters.updated, clock);

  // Name, slug, folder and model — the four things written on a deck card, so
  // anything a person can SEE on the grid is something they can type. The model
  // is matched on both forms: the label is what the card shows ("Claude ·
  // Opus"), the id is what a copied filter link carries.
  function matchesQuery(d: DeckSummary) {
    if (!q) return true;
    const model = deckModelId(d);
    return (
      d.title.toLowerCase().includes(q) ||
      d.slug.toLowerCase().includes(q) ||
      (!!d.category && d.category.toLowerCase().includes(q)) ||
      (!!model && model.includes(q)) ||
      (formatBuiltByShort(d.builtBy) || '').toLowerCase().includes(q)
    );
  }
  // A selected parent shows itself + all its children; the sentinel shows only
  // uncategorised decks; null shows everything.
  function matchesActiveCategory(d: DeckSummary) {
    const activeCategory = filters.category;
    if (activeCategory === null) return true;
    if (activeCategory === UNCATEGORISED) return !d.category;
    return !!d.category && categoryMatches(d.category, activeCategory);
  }
  // Trash is its own axis — when active it replaces the deck grid with the
  // list of soft-deleted decks, so no normal deck should pass the filter.
  const showTrash = filters.status === 'trash';
  function matchesStatus(d: DeckSummary) {
    if (showTrash) return false;
    if (filters.status === 'published') return isPublished(d);
    if (filters.status === 'unpublished') return !isPublished(d);
    return true;
  }
  function matchesModel(d: DeckSummary) {
    if (!filters.model) return true;
    return deckModelId(d) === filters.model;
  }
  function matchesOwner(d: DeckSummary) {
    if (!filters.owner) return true;
    // `me` is resolved HERE and not at parse time, so the same URL means "your
    // decks" to whoever opens it. An unresolved `me` matches nothing: better a
    // momentarily empty grid than one deck-shaped lie about whose it is.
    const wanted = filters.owner === ME ? meId : filters.owner;
    return !!wanted && d.ownerId === wanted;
  }
  function matchesUpdated(d: DeckSummary) {
    if (updatedSince === null) return true;
    return d.lastEditedAt >= updatedSince;
  }

  const activeFilters = activeFilterCount(filters);
  const filtering = activeFilters > 0;
  const flatDecks = visibleDecks
    .filter(
      (d) =>
        matchesQuery(d) &&
        matchesActiveCategory(d) &&
        matchesStatus(d) &&
        matchesModel(d) &&
        matchesOwner(d) &&
        matchesUpdated(d),
    )
    .sort(DECK_SORT_COMPARATORS[filters.sort]);
  // The selection can outlive a filter change, so the bar counts (and the verbs
  // act on) only what the current view actually shows.
  const selectedCount = flatDecks.filter((d) => selected.has(d.slug)).length;

  return {
    visibleDecks,
    flatDecks,
    categoryTree,
    allCategories,
    uncategorisedCount,
    publishedCount,
    unpublishedCount,
    draftCount: unpublishedCount,
    models,
    showTrash,
    filtering,
    activeFilters,
    selectedCount,
  };
}
